import { useEffect, useRef, useState } from "react";
import Badge from "../components/badge/Badge";

import Button from "../components/button/Button";
import Preview from "../components/preview/Preview";
import { ReactComponent as PlayIcon } from "../assets/icons/play.svg";
import Paragraph from "../components/paragraph/Paragraph";
import useAuthPersistStore from "../store/authPersistStore";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import isEmpty from "lodash.isempty";
import Empty from "../components/empty/Empty";
import Share from "../components/share/Share";
import VideoPlayer from "../components/player/VideoPlayer";
import Carousel from "../components/carousel/Carousel";
import { ContentFormatType, EducatorType, EpisodeType } from "../types";
import { getImageRoute } from "../utils/getImageRoute";
import { useContentFormats } from "../hooks/useContentFormats";
import { useSerial } from "../hooks/useSerial";
import Spinner from "../components/spinner/Spinner";
import { ROUTES } from "../constants/routes";
import { useSubscriptionDetails } from "../hooks/useSubscriptionDetails";
import { useMyContent } from "../hooks/useMyContent";
import { ReactComponent as CartIcon } from "../assets/icons/cart.svg";

const Serial = () => {
  const headerRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthPersistStore();
  const [isPlaying, setIsPlaying] = useState(false);
  const [hideLight, setHideLight] = useState(false);
  const { state } = useLocation();
  const { data } = useContentFormats();
  const { id } = useParams();
  const { data: serial, isLoading } = useSerial(id);
  const [currentContentFormat, setcurrentContentFormat] = useState<
    ContentFormatType | undefined
  >();

  const { data: subscriptionDetails } = useSubscriptionDetails({
    isAuthenticated,
  });
  const { data: myContent } = useMyContent({ isAuthenticated });
  const currentSerial = state || serial;

  useEffect(() => {
    setcurrentContentFormat(
      data?.find((item) => item.id === currentSerial?.contentFormatId)
    );
  }, [currentSerial, data]);

  useEffect(() => {
    if (!isLoading && !currentSerial?.id) {
      navigate(ROUTES.home);
    }
  }, [currentSerial?.id, isLoading, navigate]);

  const shouldShowPlayButton = () => {
    return isPlayButtonDisabled() || !isAuthenticated;
  };

  const handleStartFromBeginning = () => {
    if (shouldShowPlayButton()) {
      navigate(ROUTES.subscription);
      return;
    }

    if (isEmpty(currentSerial?.episodes)) {
      return;
    }

    const firstEpisode =
      currentSerial?.episodes && currentSerial?.episodes[0]?.id;
    navigate(`/serial/${serial?.id}/episode/${firstEpisode}`, {
      state: {
        episode: { ...currentSerial?.episodes[0] },
        serial: { ...currentSerial },
      },
    });
  };

  const togglePlayback = () => {
    setHideLight(true);
    setIsPlaying(!isPlaying);
  };

  if (isLoading && !state) {
    return <Spinner />;
  }

  const isPlayButtonDisabled = () => {
    if (!currentSerial?.stripePriceId) {
      return (
        isEmpty(currentSerial?.episodes) ||
        !isAuthenticated ||
        !subscriptionDetails?.subscriptionType ||
        subscriptionDetails?.subscriptionAtRisk
      );
    }

    if (myContent?.includes(currentSerial?.id)) {
      return false;
    }

    if (
      subscriptionDetails?.subscriptionType &&
      !subscriptionDetails?.subscriptionAtRisk
    ) {
      return false;
    }

    return true;
  };

  return (
    <div className="serial">
      <div className="serial_content">
        <div className="serial_hero">
          <div className="serial_cover">
            <VideoPlayer
              isPlaying={isPlaying}
              hideLight={hideLight}
              url={currentSerial?.trailerYoutubeLink}
              light={
                <img
                  alt={currentSerial?.title}
                  src={getImageRoute(currentSerial?.horizontalCoverImage)}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center",
                  }}
                />
              }
            />
          </div>
          <div className="serial_details" ref={headerRef}>
            <div className="serial_rating">
              {currentContentFormat && (
                <Badge
                  variant="outlined"
                  size="md"
                  contentFormat={currentContentFormat}
                  onClick={() =>
                    navigate(
                      `${ROUTES.content}?contentFormatIds=${currentContentFormat?.id}`
                    )
                  }
                />
              )}
            </div>
            <div className="serial_info">
              <h1 className="serial_title">{currentSerial?.title}</h1>
              <Paragraph variant="p2" className="serial_description">
                {currentSerial?.description}
              </Paragraph>
              <Paragraph variant="p2" className="serial_educator">
                {currentSerial?.educators?.length > 1 ? (
                  <>Edukatori: </>
                ) : (
                  <>Edukator: </>
                )}
                {currentSerial?.educators.map(
                  (educator: EducatorType, index: number) => {
                    return (
                      <span
                        key={educator?.fullName}
                        onClick={() =>
                          navigate(`${ROUTES.educator}/${educator?.id}`)
                        }
                      >
                        {educator?.fullName}
                        {index < currentSerial?.educators.length - 1
                          ? ", "
                          : ""}
                      </span>
                    );
                  }
                )}
              </Paragraph>
            </div>
            <div className="serial_cta">
              <div className="serial_cta-actions">
                <Button
                  label="Trejler"
                  variant="blue"
                  leftIcon={<PlayIcon />}
                  onClick={togglePlayback}
                />
                <Button
                  label={
                    shouldShowPlayButton()
                      ? "Odaberi plan"
                      : "Gledaj od početka"
                  }
                  variant="primary"
                  leftIcon={
                    shouldShowPlayButton() ? <CartIcon /> : <PlayIcon />
                  }
                  onClick={handleStartFromBeginning}
                />
              </div>
              <Share />
            </div>
          </div>
        </div>

        <div className="serial_episodes">
          <div className="serial_episodes-header">
            <h2 className="section-title">Epizode</h2>
            {!isEmpty(currentSerial?.episodes) && (
              <span className="serial_episodes-count">
                {currentSerial?.episodeCount}
              </span>
            )}
          </div>
          <div className="serial_episodes-carousel">
            {isEmpty(currentSerial?.episodes) ? (
              <Empty emptySlide={{ type: "vertical" }} />
            ) : (
              <Carousel
                items={currentSerial?.episodes}
                component={(item: EpisodeType, index: number) => (
                  <Preview
                    variant="vertical"
                    episode={item}
                    serial={currentSerial}
                    index={index}
                    isDisabled={isPlayButtonDisabled()}
                  />
                )}
                options={{
                  gap: 12,
                  padding: 0,
                  perMove: 1,
                  fixedWidth: 280,
                  autoWidth: false,
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Serial;
